import FullPhoto from "@/components/FullPhoto";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { optionLabel } from "@/lib/profileLabels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Chips({ list, kind, lang }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map((k) => (
        <span key={k} className="px-3 py-1 rounded-full text-xs border" style={{ borderColor: "hsl(var(--nvl-border))" }}>
          {optionLabel(lang, kind, k)}
        </span>
      ))}
    </div>
  );
}

function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "hsl(var(--nvl-border))" }}>
      <span style={{ color: "hsl(var(--nvl-ink-soft))" }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

export default function ProfilePreview() {
  const { lang } = useI18n();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/preview");
        setPreview(data.preview || {});
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, []);

  if (error) {
    return <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</div>;
  }
  if (!preview) {
    return <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>{lang === "it" ? "Caricamento…" : "Loading…"}</div>;
  }

  const photos = preview.photos || [];
  const mainPhoto = photos.find((p) => p.is_main) || photos[0];
  const mainUrl = mainPhoto?.url?.startsWith("http") ? mainPhoto.url : (mainPhoto ? `${BACKEND_URL}${mainPhoto.url}` : null);

  return (
    <div className="max-w-3xl mx-auto" data-testid="profile-preview-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/app/profile/edit" className="text-xs underline flex items-center" data-testid="preview-back">
          <ArrowLeft size={14} className="mr-1" /> {lang === "it" ? "Torna a modifica" : "Back to edit"}
        </Link>
        <span className="ml-auto text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {lang === "it" ? "Come ti vedono gli altri" : "How others see you"}
        </span>
      </div>

      <div className="nvl-card overflow-hidden">
        <FullPhoto src={mainUrl} className="max-h-[70vh]" />
        <div
          className="relative w-full flex items-end p-8"
          style={{
            backgroundColor: "hsl(var(--nvl-ink))",
            backgroundSize: "cover", backgroundPosition: "center",
            color: "hsl(var(--nvl-bg))",
          }}
        >
          <div className="absolute inset-0" style={{ background: mainUrl ? "linear-gradient(180deg, transparent 40%, rgba(10,17,40,0.75))" : undefined }} />
          <div className="relative">
            <h1 className="font-display text-4xl md:text-5xl leading-tight">
              {preview.display_name}{preview.age ? <span className="opacity-80">, {preview.age}</span> : null}
            </h1>
            {preview.city ? (
              <p className="mt-2 text-sm flex items-center gap-2 opacity-90">
                <MapPin size={14} /> {preview.city}{preview.country ? `, ${preview.country}` : ""}
              </p>
            ) : null}
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          {preview.bio ? (
            <div>
              <p className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Su di me" : "About me"}</p>
              <p className="text-sm md:text-base" data-testid="preview-bio">{preview.bio}</p>
            </div>
          ) : null}

          {photos.length > 1 ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Foto" : "Photos"}</p>
              <div className="grid grid-cols-3 gap-2">
                {photos.filter((photo) => photo !== mainPhoto).map((p) => {
                  const u = p.url?.startsWith("http") ? p.url : `${BACKEND_URL}${p.url}`;
                  return <FullPhoto key={p.id || p.url} src={u} className="aspect-square rounded-md" />;
                })}
              </div>
            </div>
          ) : null}

          {preview.top_values?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Valori principali" : "Top values"}</p>
              <Chips list={preview.top_values} kind="values" lang={lang} />
            </div>
          ) : null}

          {preview.interests?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Interessi" : "Interests"}</p>
              <Chips list={preview.interests} kind="interests" lang={lang} />
            </div>
          ) : null}

          {preview.relationship_goals?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Cosa cerca" : "Looking for"}</p>
              <Chips list={preview.relationship_goals} kind="relationship_goals" lang={lang} />
            </div>
          ) : null}

          {preview.communication_style?.length ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Comunicazione" : "Communication"}</p>
              <Chips list={preview.communication_style} kind="communication_style" lang={lang} />
            </div>
          ) : null}

          {preview.lifestyle && Object.values(preview.lifestyle).some(Boolean) ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Stile di vita" : "Lifestyle"}</p>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <KV label={lang==="it"?"Vita":"Lifestyle"} value={preview.lifestyle.lifestyle_type ? optionLabel(lang,"lifestyle_type",preview.lifestyle.lifestyle_type) : null} />
                <KV label={lang==="it"?"Fumo":"Smoking"} value={preview.lifestyle.smoking ? optionLabel(lang,"smoking",preview.lifestyle.smoking) : null} />
                <KV label={lang==="it"?"Alcol":"Alcohol"} value={preview.lifestyle.alcohol ? optionLabel(lang,"alcohol",preview.lifestyle.alcohol) : null} />
                <KV label={lang==="it"?"Animali":"Pets"} value={preview.lifestyle.pets ? optionLabel(lang,"pets",preview.lifestyle.pets) : null} />
                <KV label={lang==="it"?"Alimentazione":"Diet"} value={preview.lifestyle.diet ? optionLabel(lang,"diet",preview.lifestyle.diet) : null} />
              </div>
            </div>
          ) : null}

          {preview.family && Object.values(preview.family).some(Boolean) ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Famiglia" : "Family"}</p>
              <div className="grid sm:grid-cols-2 gap-x-6">
                <KV label={lang==="it"?"Stato":"Status"} value={preview.family.relationship_status ? optionLabel(lang,"relationship_status",preview.family.relationship_status) : null} />
                <KV label={lang==="it"?"Figli":"Children"} value={preview.family.children ? optionLabel(lang,"children",preview.family.children) : null} />
                <KV label={lang==="it"?"Vuole figli":"Wants children"} value={preview.family.wants_children ? optionLabel(lang,"wants_children",preview.family.wants_children) : null} />
              </div>
            </div>
          ) : null}

          {preview.religion ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Religione" : "Religion"}</p>
              <p className="text-sm">{optionLabel(lang, "religion", preview.religion.religion)}</p>
            </div>
          ) : null}

          {preview.community?.covid_perspective ? (
            <div>
              <p className="nvl-overline mb-3" style={{ color: "hsl(var(--nvl-accent))" }}>{lang === "it" ? "Prospettiva sulla community" : "Community perspective"}</p>
              <p className="text-sm">{optionLabel(lang, "covid_perspective", preview.community.covid_perspective)}</p>
              {preview.community.covid_motivations?.length ? (
                <div className="mt-3"><Chips list={preview.community.covid_motivations} kind="covid_motivations" lang={lang} /></div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
